import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  useTheme,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { login, employeeLogin } from "store/features/auth/authActions";
import { getEmployeeCompanies } from "services/authService";
import { selectAuthLoading } from "store/features/auth/authSelectors";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";
import { fetchUserSubscription } from "store/features/subscription/subscriptionActions";
import {
  getSecureLocalItem,
  setSecureLocalItem,
  removeSecureLocalItem,
} from "utils/secureSessionStorage";
import loginGirl from "assets/login/login-girl.svg";

export default function SignIn() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectAuthLoading);

  // Remember me states
  const [loginType, setLoginType] = useState("company"); // "company" or "employee"
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phone: "",
  });

  // Multi-company employee login states
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [isCheckingCompanies, setIsCheckingCompanies] = useState(false);
  const [companyCheckError, setCompanyCheckError] = useState("");
  const [hasCheckedCompanies, setHasCheckedCompanies] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState({
    identifier: false,
    password: false,
    company: false,
  });

  // Auto-fetch active companies when 10-digit phone number is entered in employee mode
  useEffect(() => {
    if (loginType !== "employee") {
      setCompanies([]);
      setSelectedCompanyId("");
      setCompanyCheckError("");
      setHasCheckedCompanies(false);
      return;
    }

    if (formData.phone.length === 10) {
      let isMounted = true;
      const fetchCompanies = async () => {
        setIsCheckingCompanies(true);
        setCompanyCheckError("");
        try {
          const response = await getEmployeeCompanies(formData.phone);
          if (!isMounted) return;

          const fetchedCompanies = response?.data?.companies || [];
          setCompanies(fetchedCompanies);
          setHasCheckedCompanies(true);

          if (fetchedCompanies.length === 0) {
            setCompanyCheckError(
              response?.meta?.message ||
                "No active company found for this mobile number."
            );
            setSelectedCompanyId("");
          } else if (fetchedCompanies.length === 1) {
            // Auto-select company without displaying dropdown
            setSelectedCompanyId(fetchedCompanies[0].tenant_id);
            setCompanyCheckError("");
          } else {
            // Multiple companies: default to first company
            setSelectedCompanyId(fetchedCompanies[0].tenant_id);
            setCompanyCheckError("");
          }
        } catch (error) {
          if (!isMounted) return;
          const message =
            error?.meta?.message ||
            error?.message ||
            "Failed to verify company membership";
          setCompanyCheckError(message);
          setCompanies([]);
          setSelectedCompanyId("");
          setHasCheckedCompanies(true);
        } finally {
          if (isMounted) setIsCheckingCompanies(false);
        }
      };

      fetchCompanies();

      return () => {
        isMounted = false;
      };
    } else {
      setCompanies([]);
      setSelectedCompanyId("");
      setCompanyCheckError("");
      setHasCheckedCompanies(false);
    }
  }, [formData.phone, loginType]);

  useEffect(() => {
    const savedRememberMe = getSecureLocalItem("rememberMe") === true;
    setRememberMe(savedRememberMe);

    if (savedRememberMe) {
      const savedEmail = getSecureLocalItem("savedEmail");
      if (savedEmail) {
        setFormData((prev) => ({ ...prev, email: savedEmail }));
      }
    }
  }, []);

  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);

    if (checked) {
      setSecureLocalItem("rememberMe", true);
      if (formData.email) {
        setSecureLocalItem("savedEmail", formData.email);
      }
    } else {
      removeSecureLocalItem("rememberMe");
      removeSecureLocalItem("savedEmail");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Direct numeric enforcement for the phone input (max 10 digits)
    if (name === "phone") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      if (numericValue.length < 10) {
        setCompanies([]);
        setSelectedCompanyId("");
        setCompanyCheckError("");
        setHasCheckedCompanies(false);
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email" && rememberMe) {
      setSecureLocalItem("savedEmail", value);
    }
  };

  const handleTabSwitch = (type) => {
    setLoginType(type);
    setCompanies([]);
    setSelectedCompanyId("");
    setCompanyCheckError("");
    setHasCheckedCompanies(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loginType === "employee") {
      if (formData.phone.length !== 10) {
        showErrorToast("Please enter a valid 10-digit mobile number");
        return;
      }
      if (isCheckingCompanies) {
        showErrorToast("Verifying company membership, please wait...");
        return;
      }
      if (hasCheckedCompanies && companies.length === 0) {
        showErrorToast(
          companyCheckError || "No active company found for this mobile number"
        );
        return;
      }
      if (companies.length > 1 && !selectedCompanyId) {
        showErrorToast("Please select a company to log in");
        return;
      }
    }

    if (rememberMe && formData.email && loginType === "company") {
      setSecureLocalItem("savedEmail", formData.email);
      setSecureLocalItem("rememberMe", true);
    }
    const result =
      loginType === "company"
        ? await dispatch(
            login({ email: formData.email, password: formData.password }),
          )
        : await dispatch(
            employeeLogin({
              phone: formData.phone,
              password: formData.password,
              tenant_id: selectedCompanyId || undefined,
            }),
          );

    if (result?.success) {
      const role = getSecureLocalItem("role");

      if (role === "SUPER_ADMIN") {
        showSuccessToast("Welcome Super Admin!");
        navigate("/admin/dashboard");
      } else if (role === "Company" || role === "Employee") {
        const subscriptionResult = await dispatch(fetchUserSubscription());

        if (subscriptionResult.success) {
          navigate("/company/dashboard");
        } else {
          showSuccessToast("Please select a subscription plan to continue.");
          navigate("/subscription/plans");
        }
      } else {
        showErrorToast("Sorry! Something went wrong");
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "448px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%" }}
      >
        <Paper
          elevation={24}
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: "24px",
            p: { xs: 3, lg: 4 },
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 251, 235, 0.95) 100%)",
            backdropFilter: "blur(16px)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(245, 158, 11, 0.15)"
                : "rgba(245, 158, 11, 0.2)"
            }`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 25px 50px -12px rgba(0, 0, 0, 0.5)"
                : "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
            ml: { lg: "-5%" },
          }}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: "24px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(15, 23, 42, 0.8)"
                    : "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(4px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <CircularProgress size={48} sx={{ color: "primary.main" }} />
              <Typography sx={{ mt: 2, color: "primary.main", fontWeight: 500 }}>
                Signing in...
              </Typography>
            </Box>
          )}

          {/* Decorative glow effects */}
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -50,
              left: -50,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Top Right Illustration */}
          <Box
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              opacity: 0.9,
              ...(theme.breakpoints.up("lg") && {
                right: 24,
                top: 24,
              }),
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Box
                component="img"
                src={loginGirl}
                alt="Login illustration"
                sx={{
                  width: { xs: 64, lg: 80 },
                  height: { xs: 64, lg: 80 },
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
                }}
              />
            </motion.div>
          </Box>

          {/* Header & Switch Segment */}
          <Box sx={{ mb: 4, textAlign: "left" }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.875rem", lg: "2.25rem" },
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Welcome back!
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                }}
              >
                Sign in to your account
              </Typography>
            </motion.div>

            {/* Smooth Animated Sliding Toggle */}
            <Box
              component={motion.div}
              layout
              sx={{
                position: "relative",
                display: "flex",
                borderRadius: "100px",
                overflow: "hidden",
                width: "fit-content",
                boxShadow: "0 0 0 1px #F09A0B",
                backgroundColor: "transparent",
                p: "2px",
              }}
            >
              {/* Sliding Active Pill */}
              <Box
                component={motion.div}
                layout
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                sx={{
                  position: "absolute",
                  top: "2px",
                  bottom: "2px",
                  left: loginType === "company" ? "2px" : "50%",
                  width: "calc(50% - 2px)",
                  backgroundColor: "primary.main",
                  borderRadius: "100px",
                  zIndex: 0,
                }}
              />

              <Button
                onClick={() => handleTabSwitch("company")}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  borderRadius: "100px",
                  px: 4,
                  py: 0.5,
                  minHeight: 0,
                  color:
                    loginType === "company"
                      ? "primary.contrastText"
                      : "primary.main",
                  textTransform: "none",
                  fontWeight: 600,
                  transition: "color 0.2s ease",
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                Company
              </Button>

              <Button
                onClick={() => handleTabSwitch("employee")}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  borderRadius: "100px",
                  px: 4,
                  py: 0.5,
                  minHeight: 0,
                  color:
                    loginType === "employee"
                      ? "primary.contrastText"
                      : "primary.main",
                  textTransform: "none",
                  fontWeight: 600,
                  transition: "color 0.2s ease",
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                Employee
              </Button>
            </Box>
          </Box>

          {/* Form Context */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Identifier Field (Email / Phone Toggle Layout) */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <AnimatePresence mode="wait">
                  <Box
                    key={loginType}
                    component={motion.div}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.15 }}
                  >
                    <TextField
                      fullWidth
                      name={loginType === "company" ? "email" : "phone"}
                      type={loginType === "company" ? "email" : "tel"}
                      label={
                        loginType === "company"
                          ? "Email Address"
                          : "Phone Number"
                      }
                      value={
                        loginType === "company"
                          ? formData.email
                          : formData.phone
                      }
                      onChange={handleChange}
                      onFocus={() =>
                        setIsFocused({ ...isFocused, identifier: true })
                      }
                      onBlur={() =>
                        setIsFocused({ ...isFocused, identifier: false })
                      }
                      required
                      placeholder={
                        loginType === "company"
                          ? "Enter your email"
                          : "Enter your phone number"
                      }
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          color: isFocused.identifier
                            ? "#f59e0b"
                            : theme.palette.mode === "dark"
                            ? "#94a3b8"
                            : "#6b7280",
                          fontWeight: 500,
                          "&.Mui-focused": { color: "primary.main" },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {loginType === "company" ? (
                              <EmailIcon
                                sx={{
                                  color: isFocused.identifier
                                    ? "#f59e0b"
                                    : theme.palette.mode === "dark"
                                    ? "#64748b"
                                    : "#9ca3af",
                                }}
                              />
                            ) : (
                              <PhoneIcon
                                sx={{
                                  color: isFocused.identifier
                                    ? "#f59e0b"
                                    : theme.palette.mode === "dark"
                                    ? "#64748b"
                                    : "#9ca3af",
                                }}
                              />
                            )}
                          </InputAdornment>
                        ),
                        endAdornment: loginType === "employee" && (
                          <InputAdornment position="end">
                            {isCheckingCompanies ? (
                              <CircularProgress
                                size={18}
                                sx={{ color: "#f59e0b", mr: 0.5 }}
                              />
                            ) : hasCheckedCompanies && companies.length > 0 ? (
                              <CheckCircleIcon
                                sx={{ color: "#10b981", fontSize: 20, mr: 0.5 }}
                              />
                            ) : null}
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: "14px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: companyCheckError
                              ? "#ef4444"
                              : isFocused.identifier
                              ? "#f59e0b"
                              : theme.palette.mode === "dark"
                              ? "#334155"
                              : "#e5e7eb",
                            borderWidth:
                              isFocused.identifier || companyCheckError
                                ? "2px"
                                : "1px",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: companyCheckError ? "#ef4444" : "#f59e0b",
                          },
                          transition: "all 0.2s",
                          transform: isFocused.identifier
                            ? "scale(1.01)"
                            : "scale(1)",
                          backgroundColor: isFocused.identifier
                            ? theme.palette.mode === "dark"
                              ? "rgba(30, 41, 59, 0.8)"
                              : "rgba(255, 255, 255, 0.9)"
                            : "transparent",
                        },
                      }}
                      helperText={
                        loginType === "employee" ? (
                          companyCheckError ? (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ color: "#ef4444", fontWeight: 500 }}
                            >
                              {companyCheckError}
                            </Typography>
                          ) : hasCheckedCompanies && companies.length === 1 ? (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ color: "#10b981", fontWeight: 500 }}
                            >
                              Company: {companies[0].company_name}
                            </Typography>
                          ) : hasCheckedCompanies && companies.length > 1 ? (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ color: "#f59e0b", fontWeight: 500 }}
                            >
                              {companies.length} companies found (please choose below)
                            </Typography>
                          ) : formData.phone.length > 0 &&
                            formData.phone.length < 10 ? (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#94a3b8"
                                    : "#6b7280",
                              }}
                            >
                              Enter 10-digit mobile number (
                              {10 - formData.phone.length} left)
                            </Typography>
                          ) : null
                        ) : null
                      }
                      sx={{
                        "& .MuiInputBase-input": {
                          py: "14px",
                          fontSize: "0.95rem",
                        },
                        "& .MuiFormHelperText-root": {
                          mx: 1,
                          mt: 0.75,
                        },
                      }}
                    />
                  </Box>
                </AnimatePresence>
              </motion.div>

              {/* Company Selection Field (Only shown when employee is active in multiple companies) */}
              {loginType === "employee" && companies.length > 1 && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -8 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                >
                  <FormControl fullWidth variant="outlined">
                    <InputLabel
                      shrink
                      id="employee-company-select-label"
                      sx={{
                        color: isFocused.company
                          ? "#f59e0b"
                          : theme.palette.mode === "dark"
                          ? "#94a3b8"
                          : "#6b7280",
                        fontWeight: 500,
                        "&.Mui-focused": { color: "primary.main" },
                      }}
                    >
                      Select Company
                    </InputLabel>
                    <Select
                      labelId="employee-company-select-label"
                      id="employee-company-select"
                      value={selectedCompanyId}
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                      onFocus={() =>
                        setIsFocused((prev) => ({ ...prev, company: true }))
                      }
                      onBlur={() =>
                        setIsFocused((prev) => ({ ...prev, company: false }))
                      }
                      label="Select Company"
                      startAdornment={
                        <InputAdornment position="start">
                          <BusinessIcon
                            sx={{
                              color: isFocused.company
                                ? "#f59e0b"
                                : theme.palette.mode === "dark"
                                ? "#64748b"
                                : "#9ca3af",
                            }}
                          />
                        </InputAdornment>
                      }
                      sx={{
                        borderRadius: "14px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isFocused.company
                            ? "#f59e0b"
                            : theme.palette.mode === "dark"
                            ? "#334155"
                            : "#e5e7eb",
                          borderWidth: isFocused.company ? "2px" : "1px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#f59e0b",
                        },
                        transition: "all 0.2s",
                        backgroundColor: isFocused.company
                          ? theme.palette.mode === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(255, 255, 255, 0.9)"
                          : "transparent",
                        "& .MuiSelect-select": {
                          py: "14px",
                          fontSize: "0.95rem",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: "14px",
                            mt: 1,
                            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2)",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e293b"
                                : "#ffffff",
                          },
                        },
                      }}
                    >
                      {companies.map((comp) => (
                        <MenuItem
                          key={comp.tenant_id}
                          value={comp.tenant_id}
                          sx={{
                            py: 1.2,
                            px: 2,
                            borderRadius: "8px",
                            mx: 1,
                            my: 0.5,
                            "&.Mui-selected": {
                              backgroundColor:
                                "rgba(245, 158, 11, 0.15) !important",
                              fontWeight: 600,
                            },
                            "&:hover": {
                              backgroundColor: "rgba(245, 158, 11, 0.08)",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  selectedCompanyId === comp.tenant_id
                                    ? 600
                                    : 500,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#f1f5f9"
                                    : "#0f172a",
                              }}
                            >
                              {comp.company_name}
                            </Typography>
                            {comp.subdomain && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#94a3b8"
                                      : "#64748b",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {comp.subdomain}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? "#94a3b8"
                            : "#6b7280",
                        mx: 1,
                      }}
                    >
                      Choose the company you want to log in to
                    </FormHelperText>
                  </FormControl>
                </motion.div>
              )}

              {/* Password Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Box sx={{ position: "relative", width: "100%" }}>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() =>
                      setIsFocused({ ...isFocused, password: true })
                    }
                    onBlur={() =>
                      setIsFocused({ ...isFocused, password: false })
                    }
                    required
                    placeholder="Enter your password"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        color: isFocused.password
                          ? "#f59e0b"
                          : theme.palette.mode === "dark"
                          ? "#94a3b8"
                          : "#6b7280",
                        fontWeight: 500,
                        "&.Mui-focused": { color: "primary.main" },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon
                            sx={{
                              color: isFocused.password
                                ? "#f59e0b"
                                : theme.palette.mode === "dark"
                                ? "#64748b"
                                : "#9ca3af",
                            }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: "14px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isFocused.password
                            ? "#f59e0b"
                            : theme.palette.mode === "dark"
                            ? "#334155"
                            : "#e5e7eb",
                          borderWidth: isFocused.password ? "2px" : "1px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#f59e0b",
                        },
                        transition: "all 0.2s",
                        transform: isFocused.password
                          ? "scale(1.01)"
                          : "scale(1)",
                        backgroundColor: isFocused.password
                          ? theme.palette.mode === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(255, 255, 255, 0.9)"
                          : "transparent",
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        py: "14px",
                        fontSize: "0.95rem",
                        pr: "44px",
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      color: showPassword
                        ? "#f59e0b"
                        : theme.palette.mode === "dark"
                        ? "#94a3b8"
                        : "#6b7280",
                      "&:hover": {
                        color: "primary.main",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ fontSize: 20 }} />
                    ) : (
                      <Visibility sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </Box>
              </motion.div>

              {/* Remember Me Setup */}
              {/* <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={handleRememberMeChange}
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#64748b"
                              : "#9ca3af",
                          "&.Mui-checked": { color: "primary.main" },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#94a3b8"
                              : "#6b7280",
                        }}
                      >
                        Remember me
                      </Typography>
                    }
                  />
                </Stack>
              </motion.div> */}

              {/* Submit Action */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <Button
                  type="submit"
                  disabled={
                    loading ||
                    isCheckingCompanies ||
                    (loginType === "employee" &&
                      hasCheckedCompanies &&
                      companies.length === 0)
                  }
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: "14px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      transition: "left 0.5s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 25px -5px rgba(245, 158, 11, 0.4)",
                      "&::before": { left: "100%" },
                    },
                    "&:active": { transform: "translateY(1px)" },
                    "&:disabled": { opacity: 0.7 },
                  }}
                >
                  {loading ? (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                      <Typography
                        variant="body1"
                        sx={{ color: "#ffffff", fontWeight: 500 }}
                      >
                        Signing in...
                      </Typography>
                    </Stack>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </motion.div>

              {/* Secondary Actions / Footer */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Box sx={{ mt: 2, pt: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                    }}
                  >
                    Don't have an account?{" "}
                    <Link
                      to="/auth/register"
                      style={{
                        fontWeight: 600,
                        color: "primary.main",
                        textDecoration: "none",
                        transition: "color 0.2s",
                      }}
                      onMouseEnter={(e) => (e.target.style.color = "#d97706")}
                      onMouseLeave={(e) => (e.target.style.color = "#f59e0b")}
                    >
                      Register here
                    </Link>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 2,
                      display: "block",
                      color:
                        theme.palette.mode === "dark" ? "#475569" : "#9ca3af",
                    }}
                  >
                    © {new Date().getFullYear()} EBS Office Management System
                  </Typography>
                </Box>
              </motion.div>
            </Stack>
          </form>
        </Paper>
      </motion.div>
    </Box>
  );
}
